import React, { useEffect, useRef } from 'react';

/**
 * CursorTrail — canvas-based intelligent particle trail.
 * Adds sparks that emit from the cursor, drift, and fade.
 * Skipped on touch devices and when prefers-reduced-motion is set.
 */
export default function CursorTrail() {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const particlesRef = useRef([]);
    const mouseRef = useRef({ x: 0, y: 0, active: false });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isTouch = window.matchMedia('(hover: none)').matches;
        if (reduced || isTouch) return;

        const ctx = canvas.getContext('2d');
        const setSize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;
            canvas.style.width = `${window.innerWidth}px`;
            canvas.style.height = `${window.innerHeight}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        setSize();

        const colors = ['#00F0FF', '#B026FF', '#00FF66'];
        let lastEmit = 0;

        const onMove = (e) => {
            mouseRef.current.x = e.clientX;
            mouseRef.current.y = e.clientY;
            mouseRef.current.active = true;

            const now = performance.now();
            if (now - lastEmit > 18) {
                lastEmit = now;
                for (let i = 0; i < 2; i++) {
                    particlesRef.current.push({
                        x: e.clientX + (Math.random() - 0.5) * 6,
                        y: e.clientY + (Math.random() - 0.5) * 6,
                        vx: (Math.random() - 0.5) * 0.6,
                        vy: (Math.random() - 0.5) * 0.6 - 0.15,
                        r: Math.random() * 1.8 + 0.6,
                        life: 0,
                        maxLife: 55 + Math.random() * 40,
                        color: colors[Math.floor(Math.random() * colors.length)],
                    });
                }
                if (particlesRef.current.length > 300) {
                    particlesRef.current.splice(0, particlesRef.current.length - 300);
                }
            }
        };

        const onLeave = () => (mouseRef.current.active = false);

        const tick = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const parts = particlesRef.current;
            ctx.globalCompositeOperation = 'lighter';
            for (let i = parts.length - 1; i >= 0; i--) {
                const p = parts[i];
                p.life++;
                p.x += p.vx;
                p.y += p.vy;
                p.vx *= 0.97;
                p.vy *= 0.97;

                const t = p.life / p.maxLife;
                const alpha = Math.max(0, 1 - t);
                if (alpha <= 0.02) {
                    parts.splice(i, 1);
                    continue;
                }
                ctx.beginPath();
                ctx.fillStyle = p.color;
                ctx.globalAlpha = alpha * 0.7;
                ctx.shadowColor = p.color;
                ctx.shadowBlur = 12;
                ctx.arc(p.x, p.y, p.r * (1 - t * 0.6), 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;
            rafRef.current = requestAnimationFrame(tick);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseleave', onLeave);
        window.addEventListener('resize', setSize);
        // Regression guard hook — allows tests to assert particles are emitted
        // in response to synthetic mousemove events.
        if (typeof window !== 'undefined') {
            window.__cursorTrail = {
                get particleCount() { return particlesRef.current.length; },
                get listenerAttached() { return true; },
            };
        }
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseleave', onLeave);
            window.removeEventListener('resize', setSize);
            cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return <canvas ref={canvasRef} data-testid="cursor-trail-canvas" className="cursor-trail-canvas" aria-hidden />;
}
